import { useEffect } from "react";
import useConfig from "../demos/hooks/useConfig";

/**
 * Hook that applies color configuration from config to CSS variables.
 * This enables runtime customization of the entire color system without recompilation.
 *
 * Usage: Call this hook once at the app root level (e.g., in App.tsx)
 *
 * Applies three layers of CSS variables:
 * 1. Primary color palette (--primary-50 through --primary-900)
 * 2. Theme colors (sidebar, panels, right action) - defaults derive from primary
 * 3. Button colors (btn-primary, btn-accent, btn-fab, btn-subtle)
 */
export function useButtonColors() {
  const { config } = useConfig();

  // Apply primary color palette
  useEffect(() => {
    if (!config.primaryColors) return;

    const root = document.documentElement;
    const shades = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"] as const;

    for (const shade of shades) {
      const value = config.primaryColors[shade];
      if (value) {
        root.style.setProperty(`--primary-${shade}`, value);
      }
    }
  }, [config.primaryColors]);

  // Apply theme colors (sidebar, panels, right action)
  useEffect(() => {
    if (!config.themeColors) return;

    const root = document.documentElement;
    const mapping: Record<string, string> = {
      sidebarBackground: "--sidebar-background",
      sidebarHamburgerBackground: "--sidebar-hamburger-background",
      sidebarHamburgerIcon: "--sidebar-hamburger-icon",
      sidebarTitle: "--sidebar-title",
      sidebarSubtitle: "--sidebar-subtitle",
      sidebarActiveItem: "--sidebar-active-item",
      sidebarItemHover: "--sidebar-item-hover",
      sidebarScrollbarBackground: "--sidebar-scrollbar-background",
      sidebarScrollbarThumb: "--sidebar-scrollbar-thumb",
      bottomPanelBackground: "--bottom-panel-background",
      bottomPanelTitle: "--bottom-panel-title",
      bottomPanelSubtitle: "--bottom-panel-subtitle",
      bottomPanelScrollbarBackground: "--bottom-panel-scrollbar-background",
      bottomPanelScrollbarThumb: "--bottom-panel-scrollbar-thumb",
      rightActionBackground: "--right-action-background",
      rightActionIcon: "--right-action-icon",
      searchInputTextColor: "--search-input-text-color",
    };

    for (const [key, cssVar] of Object.entries(mapping)) {
      const value = config.themeColors[key as keyof typeof config.themeColors];
      if (value) {
        root.style.setProperty(cssVar, value);
      }
    }
  }, [config.themeColors]);

  // Apply button colors
  useEffect(() => {
    if (!config.buttonColors) return;

    const root = document.documentElement;
    const { primary, accent, fab, subtle } = config.buttonColors;

    if (primary) {
      if (primary.bg) root.style.setProperty("--btn-primary-bg", primary.bg);
      if (primary.bgHover) root.style.setProperty("--btn-primary-bg-hover", primary.bgHover);
      if (primary.text) root.style.setProperty("--btn-primary-text", primary.text);
      if (primary.ring) root.style.setProperty("--btn-primary-ring", primary.ring);
    }

    if (accent) {
      if (accent.bg) root.style.setProperty("--btn-accent-bg", accent.bg);
      if (accent.bgHover) root.style.setProperty("--btn-accent-bg-hover", accent.bgHover);
      if (accent.text) root.style.setProperty("--btn-accent-text", accent.text);
      if (accent.gradientFrom) root.style.setProperty("--btn-accent-gradient-from", accent.gradientFrom);
      if (accent.gradientTo) root.style.setProperty("--btn-accent-gradient-to", accent.gradientTo);
      if (accent.ring) root.style.setProperty("--btn-accent-ring", accent.ring);
    }

    if (fab) {
      if (fab.bg) root.style.setProperty("--btn-fab-bg", fab.bg);
      if (fab.bgHover) root.style.setProperty("--btn-fab-bg-hover", fab.bgHover);
      if (fab.text) root.style.setProperty("--btn-fab-text", fab.text);
      if (fab.ring) root.style.setProperty("--btn-fab-ring", fab.ring);
    }

    if (subtle) {
      if (subtle.bg) root.style.setProperty("--btn-subtle-bg", subtle.bg);
      if (subtle.bgHover) root.style.setProperty("--btn-subtle-bg-hover", subtle.bgHover);
      if (subtle.text) root.style.setProperty("--btn-subtle-text", subtle.text);
    }
  }, [config.buttonColors]);
}

export default useButtonColors;
